// File: src/components/admin/common/AiGenerateButton.js
// Copyright (c) 2025 DefoAI UG (haftungsbeschränkt)
// All rights reserved.

import React, { useState } from 'react';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, CircularProgress, Alert } from '@mui/material';
import { AutoAwesome } from '@mui/icons-material';
import { adminApi, handleAdminApiError } from '../../../services/adminApi';

/**
 * A reusable button that triggers an AI generation flow.
 * 
 * @param {object} props
 * @param {function} props.onSuccess - Callback with generated content: (result) => void
 * @param {string} props.systemMessage - The system prompt defining the AI's role and format.
 * @param {string} props.promptContext - Optional context string attached to the user prompt.
 * @param {string} props.buttonLabel - Label for the trigger button (default: "Generate with AI").
 * @param {object} props.schema - Optional JSON Schema for structured output.
 * @param {boolean} props.jsonMode - Whether to enforce JSON object mode (default: false).
 */
const AiGenerateButton = ({
    onSuccess,
    systemMessage,
    promptContext = '',
    buttonLabel = 'Generate with AI',
    schema,
    jsonMode = false
}) => {
    const [open, setOpen] = useState(false);
    const [userPrompt, setUserPrompt] = useState('');
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState(null);

    const handleGenerate = async () => {
        if (!userPrompt.trim()) return;

        setGenerating(true);
        setError(null);
        try {
            const response = await adminApi.generateEdenAI({
                systemMessage,
                prompt: `${userPrompt}\n\nContext:\n${promptContext}`,
                schema,
                jsonMode
            });

            // If we get a result, parse if necessary (though API usually returns object if jsonMode is true)
            // But EdenAI might return text so we handle both.
            let result = response.result;
            if (jsonMode && typeof result === 'string') {
                try {
                    // Cleanup Markdown code blocks if present
                    const cleaned = result.replace(/```json\n?|```/g, '').trim();
                    result = JSON.parse(cleaned);
                } catch (e) {
                    console.error("Failed to parse JSON response:", e);
                    throw new Error("AI returned invalid JSON: " + result.substring(0, 50) + "...");
                }
            }

            onSuccess(result);
            setOpen(false);
            setUserPrompt('');
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setError(errorInfo.message);
        } finally {
            setGenerating(false);
        }
    };

    return (
        <>
            <Button
                variant="outlined"
                size="small"
                startIcon={<AutoAwesome />}
                onClick={() => setOpen(true)}
                sx={{ textTransform: 'none' }}
            >
                {buttonLabel}
            </Button>

            <Dialog open={open} onClose={() => !generating && setOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AutoAwesome color="primary" /> {buttonLabel}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Instructions for AI"
                        fullWidth
                        multiline
                        rows={3}
                        value={userPrompt}
                        onChange={(e) => setUserPrompt(e.target.value)}
                        placeholder="e.g., 'Make it punchy and professional' or 'Translate to German'"
                        disabled={generating}
                    />
                    {error && (
                        <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)} disabled={generating}>Cancel</Button>
                    <Button
                        onClick={handleGenerate}
                        variant="contained"
                        disabled={!userPrompt.trim() || generating}
                        startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <AutoAwesome />}
                    >
                        {generating ? 'Generating...' : 'Generate'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AiGenerateButton;
